/**
 * The voice channel of a talk (design-docs/0028): microphone up and her voice down over the daemon's audio
 * WebSocket, PCM16 mono 24 kHz, microphone on tag 0 (design-docs/0011). In the iOS app the native `TalkAudio`
 * plugin does this, so it keeps running with the phone locked; in a browser this file does, while the page is open.
 */
export type TalkState = 'connecting' | 'listening' | 'reconnecting' | 'stopped';

export interface TalkAudio {
  start(url: string): Promise<void>;
  stop(): Promise<void>;
  onState(fn: (s: TalkState) => void): void;
}

interface NativePlugin {
  start(opts: { url: string }): Promise<void>;
  stop(): Promise<void>;
  addListener(event: 'state', fn: (e: { state: TalkState }) => void): Promise<unknown>;
}

const native = (): NativePlugin | null =>
  (window as unknown as { Capacitor?: { Plugins?: { TalkAudio?: NativePlugin } } }).Capacitor?.Plugins?.TalkAudio ??
  null;

/** True inside the iOS app, where the talk starts by itself and survives the lock screen. */
export const inApp = () => native() !== null;

export function talkAudio(): TalkAudio {
  const n = native();
  if (!n) return new BrowserTalkAudio();
  return {
    start: (url) => n.start({ url }),
    stop: () => n.stop(),
    onState: (fn) => void n.addListener('state', (e) => fn(e.state)),
  };
}

// Microphone → 80 ms PCM16 chunks; the AudioContext runs at 24 kHz so no resampling here.
const WORKLET = `class PcmSender extends AudioWorkletProcessor {
  constructor() { super(); this.buf = new Int16Array(1920); this.n = 0; }
  process(inputs) {
    const ch = inputs[0]?.[0];
    if (!ch) return true;
    for (let i = 0; i < ch.length; i++) {
      const x = Math.max(-1, Math.min(1, ch[i]));
      this.buf[this.n++] = x < 0 ? x * 32768 : x * 32767;
      if (this.n === this.buf.length) { this.port.postMessage(this.buf.slice(0).buffer); this.n = 0; }
    }
    return true;
  }
}
registerProcessor('pcm-sender', PcmSender);`;

class BrowserTalkAudio implements TalkAudio {
  private ctx: AudioContext | null = null;
  private mic: MediaStream | null = null;
  private ws: WebSocket | null = null;
  private url = '';
  private playhead = 0;
  private stopped = false;
  private retry = 0;
  private listeners: Array<(s: TalkState) => void> = [];

  onState(fn: (s: TalkState) => void) {
    this.listeners.push(fn);
  }

  async start(url: string) {
    this.url = url;
    this.stopped = false;
    this.emit('connecting');
    this.mic = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
    this.ctx = new AudioContext({ sampleRate: 24000 });
    const blob = URL.createObjectURL(new Blob([WORKLET], { type: 'text/javascript' }));
    await this.ctx.audioWorklet.addModule(blob);
    const node = new AudioWorkletNode(this.ctx, 'pcm-sender');
    this.ctx.createMediaStreamSource(this.mic).connect(node);
    node.port.onmessage = (m: MessageEvent<ArrayBuffer>) => {
      if (this.ws?.readyState !== WebSocket.OPEN) return;
      const out = new Uint8Array(m.data.byteLength + 1);
      out[0] = 0; // microphone
      out.set(new Uint8Array(m.data), 1);
      this.ws.send(out);
    };
    this.connect();
  }

  async stop() {
    this.stopped = true;
    this.ws?.close();
    for (const t of this.mic?.getTracks() ?? []) t.stop();
    await this.ctx?.close();
    this.emit('stopped');
  }

  private connect() {
    const ws = new WebSocket(this.url);
    ws.binaryType = 'arraybuffer';
    this.ws = ws;
    ws.onmessage = (m) => {
      if (typeof m.data === 'string') {
        if ((JSON.parse(m.data) as { type?: string }).type === 'ready') {
          this.retry = 0;
          this.emit('listening');
        }
      } else this.play(m.data as ArrayBuffer);
    };
    ws.onclose = (e) => {
      if (this.stopped) return;
      if (e.code === 4004) return void this.stop(); // the talk is over on the daemon
      this.emit('reconnecting');
      setTimeout(() => this.connect(), Math.min(10_000, 500 * 2 ** this.retry++));
    };
  }

  private play(pcm: ArrayBuffer) {
    const ctx = this.ctx;
    if (!ctx) return;
    const i16 = new Int16Array(pcm);
    const buf = ctx.createBuffer(1, i16.length, 24000);
    const ch = buf.getChannelData(0);
    for (let i = 0; i < i16.length; i++) ch[i] = i16[i]! / 32768;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    this.playhead = Math.max(this.playhead, ctx.currentTime + 0.05);
    src.start(this.playhead);
    this.playhead += buf.duration;
  }

  private emit(s: TalkState) {
    for (const fn of this.listeners) fn(s);
  }
}
